/**
 * Sentry beforeSend filter — drops noise events before they are sent.
 *
 * Filtered categories:
 *  1. Aborted/canceled HTTP requests (navigation interrupts pending XHR)
 *  2. Errors originating from browser extension scripts (matched via stacktrace)
 *  3. Console-captured noise whose entire stack is a known foreign script host
 *     (the <obscura:...> headless scraper runtime) plus our own telemetry
 *     plumbing. See isForeignRuntimeConsoleNoise.
 *  4. "Non-Error promise rejection" events without a stacktrace whose rejection
 *     value matches a known external/extension-injected pattern (e.g. content
 *     filter extensions on managed Chromebooks rejecting with plain strings).
 */

// Substrings that identify rejection values from third-party scripts
// (typically content-filter, reading-aid or analytics extensions injected into
// pages on managed Chromebooks). Stored in lowercase — compared case-insensitively
// against the rejection value.
//
// Before adding a new pattern: verify it does NOT appear anywhere in our own
// source code or built bundles (ui/, webapp/src/main/resources/static,
// especially webapp/src/main/resources/static/js), so we don't accidentally
// suppress real errors from our own code. Store patterns in lowercase.
//
// "request timeout " covers a family of reading-aid extension rejections
// shaped like "Request timeout <something>Distributor.getValue" or
// "Request timeout isPredictionAvailable" — none of these strings exist
// in our own code, and we additionally require there to be no stacktrace.
const EXTERNAL_REJECTION_VALUE_PATTERNS = [
    'request timeout ',
]

// Filename prefixes used by browser extensions. Errors thrown from these are
// never ours.
const EXTENSION_FILENAME_PREFIXES = [
    'chrome-extension://',
    'moz-extension://',
    'safari-extension://',
]

// Known foreign script hosts, matched as angle-bracketed pseudo-URLs, e.g.
// "<obscura:bootstrap>" from the Obscura headless scraper runtime.
//
// Deliberately an allowlist of runtimes we have actually observed, not a
// generic /^<scheme:/ pattern. An angle-bracketed scheme is not proof of
// foreign origin: a sourceURL can be named by our own code or instrumentation
// (e.g. "<samform:meeting-editor>"), and filtering those would silently drop
// real errors. Add entries here only with a real event to back them up.
//
// Note that "<anonymous>" and "<unknown>" appear in genuine stacktraces from
// our own code and must never be listed.
const FOREIGN_RUNTIME_FILENAME = /^<obscura:[^>]+>$/i

// Our own telemetry bundle (SamformTelemetry.<format>.js, served from several
// prefixes and sometimes with a ?version= suffix).
const TELEMETRY_BUNDLE_FILENAME = /\/SamformTelemetry\.[a-z0-9]+\.js(\?|$)/i

function isExtensionFrame(frame) {
    const filename = frame.filename || ''
    return EXTENSION_FILENAME_PREFIXES.some(prefix => filename.includes(prefix))
}

function isForeignRuntimeFrame(frame) {
    return FOREIGN_RUNTIME_FILENAME.test(frame.filename || '')
}

function isTelemetryBundleFrame(frame) {
    return TELEMETRY_BUNDLE_FILENAME.test(frame.filename || '')
}

// True only for events that captureConsoleIntegration synthesised from a
// console.error call, where the whole stack is a known foreign runtime plus our
// own telemetry plumbing.
//
// The mechanism gate is what makes the stack heuristic safe. A synthetic
// console mechanism means no exception was ever thrown: the frames are just
// "who called console.error", and our telemetry frames are unavoidable
// plumbing that captureConsoleIntegration puts there itself. For a real thrown
// error (onerror, UnhandledRejection, Sentry.captureException) the same frames
// would mean the opposite -- a genuine bug in our telemetry bundle -- so those
// are never dropped here.
//
// Requiring the absence of application frames additionally keeps our own
// console.error calls, which are actionable.
function isForeignRuntimeConsoleNoise(exception) {
    const mechanism = exception.mechanism
    if (!mechanism || mechanism.type !== 'console' || mechanism.synthetic !== true) {
        return false
    }

    const frames = exception.stacktrace && exception.stacktrace.frames
    if (!Array.isArray(frames) || frames.length === 0) {
        return false
    }

    return frames.some(isForeignRuntimeFrame) &&
        frames.every(frame => isForeignRuntimeFrame(frame) || isTelemetryBundleFrame(frame))
}

export function sentryBeforeSend(event, hint) {
    const error = hint?.originalException
    if (error && typeof error === 'object' && 'code' in error) {
        const code = error.code

        if (code === 'ERR_CANCELED') {
            return null
        }

        if (code === 'ECONNABORTED') {
            const message = typeof error.message === 'string' ? error.message : ''
            const status = typeof error.response === 'object' && error.response !== null
                ? error.response.status
                : undefined

            const looksAborted =
                /aborted|abort|canceled|cancelled/i.test(message) || status === 0

            if (looksAborted) {
                return null
            }
        }
    }

    if (event.exception && event.exception.values) {
        const values = event.exception.values

        for (const exception of values) {
            if (exception.stacktrace && Array.isArray(exception.stacktrace.frames)) {
                if (exception.stacktrace.frames.some(isExtensionFrame)) {
                    return null
                }
            }
        }

        // Drop only if EVERY exception in the event is foreign-runtime console
        // noise, so a real app error reported alongside it still gets through.
        if (values.length > 0 && values.every(isForeignRuntimeConsoleNoise)) {
            return null
        }

        // Drop only if every exception in the event is an external non-error
        // rejection candidate. This avoids suppressing a real app error that
        // happens to be reported in the same event as extension noise.
        if (
            values.length > 0 &&
            values.every(isExternalNonErrorRejection)
        ) {
            return null
        }
    }

    return event
}

function isExternalNonErrorRejection(exception) {
    if (exception.type !== 'UnhandledRejection') {
        return false
    }

    const value = typeof exception.value === 'string' ? exception.value : ''
    if (!value.includes('Non-Error promise rejection captured')) {
        return false
    }

    const frames = exception.stacktrace && exception.stacktrace.frames
    if (Array.isArray(frames) && frames.length > 0) {
        return false
    }

    const lower = value.toLowerCase()
    return EXTERNAL_REJECTION_VALUE_PATTERNS.some(
        pattern => lower.includes(pattern)
    )
}
